import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faChevronDown, faCircleXmark, faSliders } from '@fortawesome/pro-regular-svg-icons';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import Label, { LabelType } from '../Label';
import CustomSwitch from '../CustomSwitch';
import CustomCheckbox from '../CustomCheckbox';
import { FilterItemProps } from '../filter/FilterItem';
import { FilterType } from './MissionList';

export interface MissionFiltersPopupProps {
  items: FilterItemProps[];
  selectedFilterType?: FilterType;
  showMissionTypeFilters?: boolean;
  onlyAcceptableMissions: boolean;
  showUpcomingMissions: boolean;
  open: boolean;
  onOpenChange?(open: boolean): void;
  onItemsChange?(selectedIds: string[]): void;
  onFilterTypeChange?(next: FilterType): void;
  onOnlyAcceptableMissionsChange?(next: boolean): void;
  onShowUpcomingMissionsChange?(next: boolean): void;
}

const MissionFiltersPopup = ({
  items,
  selectedFilterType,
  onlyAcceptableMissions,
  showUpcomingMissions,
  open,
  showMissionTypeFilters,
  onOpenChange,
  onItemsChange,
  onFilterTypeChange,
  onOnlyAcceptableMissionsChange,
  onShowUpcomingMissionsChange,
}: MissionFiltersPopupProps) => {
  const theme = useCustomTheme();
  const t = useTranslation();
  const selectedFilterBackgroundColor = theme.isDark ? theme.background.secondary?.toString() ?? '#344450' : '#e1f4ff';
  const selectedFilterTextColor = '#000000';
  const inactiveFilterBorderColor = theme.borderColorLight?.toString() ?? '#d0d5dd';
  const triggerBorderColor = theme.borderColorLight?.toString() ?? '#d7e3ec';

  const [localItems, setLocalItems] = useState<FilterItemProps[]>(items ?? []);

  useEffect(() => {
    setLocalItems(items ?? []);
  }, [items]);

  const summaryText = useMemo(() => {
    const selectedTypes: string[] = [];

    if (!onlyAcceptableMissions) {
      selectedTypes.push(t('show-unavailable-short'));
    }

    if (showUpcomingMissions) {
      selectedTypes.push(t('show-upcoming-short'));
    }

    const selectedBusinessTypes = localItems.filter((x) => x.checked).map((x) => x.name);

    if (selectedBusinessTypes.length > 0) {
      selectedTypes.push(t('filter_by_business_type') + ': ' + selectedBusinessTypes.slice(0, 3).join(', '));
    }

    if (selectedTypes.length === 0) {
      return t('Select-a-filter');
    }

    return selectedTypes.join(' • ');
  }, [onlyAcceptableMissions, showUpcomingMissions, localItems, t]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const handleFilterItemToggle = (id: string, checked: boolean) => {
    const next = localItems.map((item) => (item.id === id ? { ...item, checked } : item));
    setLocalItems(next);
    onItemsChange?.(next.filter((x) => x.checked).map((x) => x.id));
  };

  const handleSelectAll = () => {
    const shouldSelectAll = localItems.some((x) => !x.checked);
    const next = localItems.map((item) => ({ ...item, checked: shouldSelectAll }));
    setLocalItems(next);
    onItemsChange?.(next.filter((x) => x.checked).map((x) => x.id));
  };

  const contentBody = (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Label variant={LabelType.titleLargeNoCapsPrimary}>{t('Missions-filters')}</Label>
        <Pressable
          onPress={() => handleOpenChange(false)}
          hitSlop={12}
          style={{
            padding: 8,
            marginRight: -8,
          }}
        >
          <FontAwesomeIcon icon={faCircleXmark} size={24} color={theme.text.darkTurquoiseTwo.toString()} />
        </Pressable>
      </View>

      {showMissionTypeFilters && !!onFilterTypeChange && selectedFilterType !== undefined && (
        <View style={{ flexDirection: 'row', marginBottom: 12, gap: 8 }}>
          <Pressable
            onPress={() => onFilterTypeChange?.(FilterType.All)}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: selectedFilterType === FilterType.All ? theme.text.darkTurquoiseTwo : inactiveFilterBorderColor,
              backgroundColor: selectedFilterType === FilterType.All ? selectedFilterBackgroundColor : 'transparent',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Label
              style={{
                color: selectedFilterType === FilterType.All ? selectedFilterTextColor : theme.text.primary,
                fontWeight: '600',
              }}
            >
              {t('All')}
            </Label>
          </Pressable>

          <Pressable
            onPress={() => onFilterTypeChange?.(FilterType.Classic)}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: selectedFilterType === FilterType.Classic ? theme.text.darkTurquoiseTwo : inactiveFilterBorderColor,
              backgroundColor: selectedFilterType === FilterType.Classic ? selectedFilterBackgroundColor : 'transparent',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Label
              style={{
                color: selectedFilterType === FilterType.Classic ? selectedFilterTextColor : theme.text.primary,
                fontWeight: '600',
              }}
            >
              {t('Classic')}
            </Label>
          </Pressable>

          <Pressable
            onPress={() => onFilterTypeChange?.(FilterType.Flash)}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: selectedFilterType === FilterType.Flash ? theme.text.darkTurquoiseTwo : inactiveFilterBorderColor,
              backgroundColor: selectedFilterType === FilterType.Flash ? selectedFilterBackgroundColor : 'transparent',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Label
              style={{
                color: selectedFilterType === FilterType.Flash ? selectedFilterTextColor : theme.text.primary,
                fontWeight: '600',
              }}
            >
              {t('Flash')}
            </Label>
          </Pressable>
        </View>
      )}

      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 10,
            paddingVertical: 10,
            borderRadius: 8,
            backgroundColor: theme.background.primaryLight,
            gap: 8,
          }}
        >
          <CustomSwitch
            isEnabled={!onlyAcceptableMissions}
            onPress={(next) => {
              onOnlyAcceptableMissionsChange?.(next);
            }}
          />
          <Label style={{ flex: 1 }} variant={LabelType.caption} numberOfLines={3}>
            {t('show-unavailable-short')}
          </Label>
        </View>

        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 10,
            paddingVertical: 10,
            borderRadius: 8,
            backgroundColor: theme.background.primaryLight,
            gap: 8,
          }}
        >
          <CustomSwitch
            isEnabled={showUpcomingMissions}
            onPress={(next) => {
              onShowUpcomingMissionsChange?.(next);
            }}
          />
          <Label style={{ flex: 1 }} variant={LabelType.caption} numberOfLines={3}>
            {t('show-upcoming-short')}
          </Label>
        </View>
      </View>

      <Label variant={LabelType.titleDark} style={{ marginBottom: 8 }}>
        {t('filter_by_business_type')}
      </Label>

      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
        <Pressable onPress={handleSelectAll}>
          <Label variant={LabelType.titleDarkSmall}>{localItems.find((x) => !x.checked) ? t('select_all') : t('deselect_all')}</Label>
        </Pressable>
      </View>

      <ScrollView style={{ maxHeight: 260 }}>
        {localItems.map((item) => (
          <CustomCheckbox
            key={item.id}
            small
            id={`mission-filter-${item.id}`}
            checked={!!item.checked}
            value={item.id}
            label={item.name}
            style={{ paddingVertical: 6 }}
            onChange={(checked) => handleFilterItemToggle(item.id, checked)}
          />
        ))}
      </ScrollView>
    </>
  );

  return (
    <View style={{ position: 'relative' }}>
      <Pressable
        onPress={() => handleOpenChange(true)}
        accessibilityRole="button"
        accessibilityLabel={t('Missions-filters') || t('Filters')}
        accessibilityHint={t('Select-a-filter')}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: 8,
          backgroundColor: theme.background.primaryLight,
          borderWidth: 1,
          borderColor: triggerBorderColor,
          paddingHorizontal: 12,
          paddingVertical: 10,
          gap: 10,
          minHeight: 44,
          opacity: pressed ? 0.85 : 1,
        })}
      >
        <FontAwesomeIcon icon={faSliders} size={18} color={theme.text.darkTurquoiseTwo.toString()} style={{ marginRight: 2 }} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Label
            style={{
              color: theme.text.darkTurquoiseTwo,
              fontSize: 14,
              fontWeight: '600',
            }}
            numberOfLines={2}
          >
            {summaryText}
          </Label>
        </View>
        <FontAwesomeIcon icon={faChevronDown} size={14} color={theme.text.darkTurquoiseTwo.toString()} />
      </Pressable>

      <Modal transparent animationType="fade" visible={open} onRequestClose={() => handleOpenChange(false)}>
        <Pressable
          onPress={() => handleOpenChange(false)}
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.4)',
            justifyContent: 'flex-end',
          }}
        >
          <Pressable
            onPress={() => {}}
            style={{
              backgroundColor: theme.background.primary,
              paddingHorizontal: 16,
              paddingTop: 16,
              paddingBottom: Platform.OS === 'ios' ? 24 : 16,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80%',
            }}
          >
            <View style={{ alignItems: 'center', marginBottom: 12 }}>
              <View
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: inactiveFilterBorderColor,
                }}
              />
            </View>
            {contentBody}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

export default MissionFiltersPopup;
